/**
 * Sequence Management — shadcn/ui redesign
 *
 * Visual changes only — all state, API calls, and handlers are preserved exactly.
 * Replaced: MUI → shadcn Card/Table/Sheet/AlertDialog/DropdownMenu/Badge/Progress
 * Added:    Per-row DropdownMenu, Sheet for SequenceBuilder, Skeleton rows,
 *           gradient stat cards in Statistics dialog, AlertDialog for delete.
 */
import React, { useState, useEffect } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  MoreHorizontal,
  BarChart3,
  CheckCircle2,
  XCircle,
  Layers,
  AlertTriangle,
  X,
} from 'lucide-react';
import {
  TemplateSequence,
  SequenceCreateRequest,
  SequenceStats,
} from '../../types/whatsapp-templates';
import whatsappTemplatesApi from '../../services/whatsapp-templates-api';
import SequenceBuilder from './SequenceBuilder';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import { Alert, AlertDescription } from '../ui/alert';
import { cn } from '../../lib/utils';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const TRIGGER_LABELS: Record<string, string> = {
  manual:         'Manual',
  session_closed: 'Session Closed',
  no_response:    'No Response',
};

// ─── Component ────────────────────────────────────────────────────────────────
interface SequenceManagementProps {
  accountId: number;
}

const SequenceManagement: React.FC<SequenceManagementProps> = ({ accountId }) => {
  const [sequences, setSequences] = useState<TemplateSequence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [builderOpen, setBuilderOpen] = useState(false);
  const [selectedSequence, setSelectedSequence] = useState<TemplateSequence | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sequenceToDelete, setSequenceToDelete] = useState<TemplateSequence | null>(null);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [selectedStats, setSelectedStats] = useState<SequenceStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // ── Data loading ──────────────────────────────────────────────────────────────
  const loadSequences = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await whatsappTemplatesApi.listSequences(accountId);
      if (response.success) {
        setSequences(response.sequences);
      } else {
        setError('Failed to load sequences');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load sequences');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSequences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  // ── Handlers — all logic preserved exactly ────────────────────────────────────
  const handleCreateSequence = async (sequenceData: SequenceCreateRequest) => {
    try {
      const response = await whatsappTemplatesApi.createSequence(accountId, sequenceData);
      if (response.success) {
        setBuilderOpen(false);
        setSelectedSequence(null);
        loadSequences();
      }
    } catch (err: any) {
      throw new Error(err.message || 'Failed to create sequence');
    }
  };

  const handleUpdateSequence = async (sequenceData: SequenceCreateRequest) => {
    if (!selectedSequence) return;
    try {
      const response = await whatsappTemplatesApi.updateSequence(selectedSequence.id, sequenceData);
      if (response.success) {
        setBuilderOpen(false);
        setSelectedSequence(null);
        loadSequences();
      }
    } catch (err: any) {
      throw new Error(err.message || 'Failed to update sequence');
    }
  };

  const handleDeleteSequence = async () => {
    if (!sequenceToDelete) return;
    try {
      await whatsappTemplatesApi.deleteSequence(sequenceToDelete.id);
      setDeleteDialogOpen(false);
      setSequenceToDelete(null);
      loadSequences();
    } catch (err: any) {
      setError(err.message || 'Failed to delete sequence');
    }
  };

  const handleViewStats = async (sequence: TemplateSequence) => {
    setLoadingStats(true);
    setStatsDialogOpen(true);
    try {
      const response = await whatsappTemplatesApi.getSequenceStats(sequence.id);
      if (response.success) {
        setSelectedStats(response.data || null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load statistics');
    } finally {
      setLoadingStats(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-4">

      {/* ── Error alert ────────────────────────────────────────────────────────── */}
      {error && (
        <Alert variant="destructive" className="relative pr-10">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
          <button
            className="absolute right-3 top-3 rounded-sm opacity-70 transition-opacity hover:opacity-100"
            onClick={() => setError(null)}
            aria-label="Dismiss error"
          >
            <X className="h-4 w-4" />
          </button>
        </Alert>
      )}

      {/* ── Sequences table card ────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">
            Automated Sequences
            {!loading && (
              <span className="ml-1.5 font-normal text-muted-foreground tabular-nums">
                ({sequences.length})
              </span>
            )}
          </CardTitle>
          <CardAction>
            <Button
              size="sm"
              onClick={() => { setSelectedSequence(null); setBuilderOpen(true); }}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Create Sequence
            </Button>
          </CardAction>
        </CardHeader>

        <CardContent className="p-0 pb-2">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-6">Name</TableHead>
                <TableHead>Steps</TableHead>
                <TableHead className="hidden sm:table-cell">Trigger</TableHead>
                <TableHead className="text-right tabular-nums">Usage</TableHead>
                <TableHead className="hidden md:table-cell">Success Rate</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10 pr-6" />
              </TableRow>
            </TableHeader>

            <TableBody>
              {/* Skeleton rows while loading */}
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell className="pl-6">
                      <div className="space-y-1.5">
                        <Skeleton className="h-3.5 w-32" />
                        <Skeleton className="h-3 w-40 opacity-60" />
                      </div>
                    </TableCell>
                    <TableCell><Skeleton className="h-5 w-16 rounded-full" /></TableCell>
                    <TableCell className="hidden sm:table-cell"><Skeleton className="h-5 w-24 rounded-full" /></TableCell>
                    <TableCell className="text-right"><Skeleton className="ml-auto h-3.5 w-10" /></TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-1.5 flex-1" />
                        <Skeleton className="h-3 w-8" />
                      </div>
                    </TableCell>
                    <TableCell><Skeleton className="h-5 w-16 rounded-full" /></TableCell>
                    <TableCell className="pr-6"><Skeleton className="ml-auto h-7 w-7 rounded-md" /></TableCell>
                  </TableRow>
                ))

              /* Empty state */
              ) : sequences.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <div className="flex flex-col items-center justify-center gap-3 py-14">
                      <div className="rounded-full bg-muted p-3">
                        <Layers className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-muted-foreground">No sequences found</p>
                        <p className="mt-0.5 text-xs text-muted-foreground/70">
                          Create your first automated sequence to get started
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setSelectedSequence(null); setBuilderOpen(true); }}
                      >
                        <Plus className="mr-1.5 h-3.5 w-3.5" />
                        Create Sequence
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>

              /* Data rows */
              ) : (
                sequences.map((sequence) => {
                  const successRate =
                    sequence.usage_count > 0
                      ? ((sequence.success_count / sequence.usage_count) * 100).toFixed(1)
                      : '0';

                  return (
                    <TableRow key={sequence.id} className="group">
                      {/* Name + description */}
                      <TableCell className="pl-6">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-sm font-medium">{sequence.name}</span>
                          {sequence.description && (
                            <span className="max-w-[200px] truncate text-xs text-muted-foreground">
                              {sequence.description}
                            </span>
                          )}
                        </div>
                      </TableCell>

                      {/* Step count */}
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {sequence.sequence_steps.length}{' '}
                          step{sequence.sequence_steps.length !== 1 ? 's' : ''}
                        </Badge>
                      </TableCell>

                      {/* Trigger type */}
                      <TableCell className="hidden sm:table-cell">
                        <Badge variant="outline" className="text-xs text-muted-foreground">
                          {TRIGGER_LABELS[sequence.trigger_type] ?? sequence.trigger_type}
                        </Badge>
                      </TableCell>

                      {/* Usage / success counts */}
                      <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                        {sequence.usage_count}
                        <span className="text-muted-foreground/50"> / </span>
                        {sequence.success_count}
                      </TableCell>

                      {/* Success rate progress bar */}
                      <TableCell className="hidden md:table-cell">
                        <div className="flex items-center gap-2">
                          <Progress value={Number(successRate)} className="h-1.5 flex-1" />
                          <span className="w-10 text-right text-xs tabular-nums text-muted-foreground">
                            {successRate}%
                          </span>
                        </div>
                      </TableCell>

                      {/* Active / Inactive badge */}
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={cn(
                            'gap-1 text-xs',
                            sequence.is_active
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                              : 'border-border text-muted-foreground',
                          )}
                        >
                          {sequence.is_active ? (
                            <><CheckCircle2 className="h-3 w-3" /> Active</>
                          ) : (
                            <><XCircle className="h-3 w-3" /> Inactive</>
                          )}
                        </Badge>
                      </TableCell>

                      {/* Per-row action menu */}
                      <TableCell className="pr-6 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100"
                              aria-label={`Actions for ${sequence.name}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem
                              onClick={() => { setSelectedSequence(sequence); setBuilderOpen(true); }}
                            >
                              <Pencil className="mr-2 h-3.5 w-3.5" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleViewStats(sequence)}>
                              <BarChart3 className="mr-2 h-3.5 w-3.5" />
                              View Statistics
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => { setSequenceToDelete(sequence); setDeleteDialogOpen(true); }}
                            >
                              <Trash2 className="mr-2 h-3.5 w-3.5" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── Sequence Builder — Sheet (slide-over) ──────────────────────────────── */}
      <Sheet
        open={builderOpen}
        onOpenChange={(open) => {
          if (!open) { setBuilderOpen(false); setSelectedSequence(null); }
        }}
      >
        <SheetContent
          side="right"
          className="flex flex-col gap-0 p-0 sm:max-w-3xl"
        >
          <SheetHeader className="shrink-0 border-b border-border px-6 py-4 pr-14">
            <SheetTitle>
              {selectedSequence ? 'Edit Sequence' : 'Create New Sequence'}
            </SheetTitle>
            <SheetDescription>
              {selectedSequence
                ? `Editing "${selectedSequence.name}"`
                : 'Build an automated message sequence for your customers'}
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <SequenceBuilder
              accountId={accountId}
              initialSequence={selectedSequence || undefined}
              onSave={selectedSequence ? handleUpdateSequence : handleCreateSequence}
              onCancel={() => { setBuilderOpen(false); setSelectedSequence(null); }}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Delete confirmation — AlertDialog ──────────────────────────────────── */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sequence</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-medium text-foreground">
                &ldquo;{sequenceToDelete?.name}&rdquo;
              </span>
              ? This will also stop all active instances of this sequence.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteSequence}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Statistics — Dialog with gradient KPI cards ──────────────────────── */}
      <Dialog
        open={statsDialogOpen}
        onOpenChange={(open) => {
          if (!open) { setStatsDialogOpen(false); setSelectedStats(null); }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Sequence Statistics</DialogTitle>
            {selectedStats && (
              <DialogDescription>{selectedStats.name}</DialogDescription>
            )}
          </DialogHeader>

          {loadingStats ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-20 rounded-lg" />
              ))}
            </div>
          ) : selectedStats ? (
            <div className="flex flex-col gap-4">

              {/* Primary KPI grid */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  { label: 'Total Started',  value: selectedStats.total_started,          cls: '' },
                  { label: 'Successes',      value: selectedStats.total_success,          cls: 'text-emerald-700' },
                  { label: 'Success Rate',   value: `${selectedStats.success_rate}%`,     cls: '' },
                  { label: 'Active',         value: selectedStats.active,                 cls: 'text-blue-700' },
                ].map((s) => (
                  <Card key={s.label} className="bg-gradient-to-t from-primary/5 to-card shadow-none">
                    <CardContent className="p-3">
                      <p className="text-xs text-muted-foreground">{s.label}</p>
                      <p className={cn('text-2xl font-bold tabular-nums', s.cls)}>{s.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Status breakdown */}
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Status Breakdown
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Completed', value: selectedStats.completed, cls: 'text-emerald-700' },
                    { label: 'Stopped',   value: selectedStats.stopped,   cls: 'text-amber-700' },
                    { label: 'Failed',    value: selectedStats.failed,    cls: 'text-red-600' },
                  ].map((s) => (
                    <Card key={s.label} className="shadow-none">
                      <CardContent className="p-3">
                        <p className="text-xs text-muted-foreground">{s.label}</p>
                        <p className={cn('text-xl font-bold tabular-nums', s.cls)}>{s.value}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

            </div>
          ) : null}

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setStatsDialogOpen(false); setSelectedStats(null); }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default SequenceManagement;
